import React, { ReactElement, ReactNode, useContext, useMemo } from 'react';
import ClientOAuth2 from 'client-oauth2';
import { authCallback } from '@e2m-customer-portal/core';
import { useOpenIdConfig } from '../use-open-id-config/use-open-id-config';
import { AuthClientContext } from './auth-client-context';

export function useAuthClient(): ClientOAuth2 {
  const client = useContext(AuthClientContext);
  if (!client) {
    throw new Error('No auth client found. Wrap useAuthClient in AuthClientProvider.');
  }
  return client;
}

export interface AuthClientProviderProps {
  children?: ReactNode;
}

export function AuthClientProvider({ children }: Readonly<AuthClientProviderProps>): ReactElement {
  const openIdConfig = useOpenIdConfig();

  const client = useMemo(() => {
    const { protocol, hostname, port } = window.location;

    let authority = `//${hostname}`;
    if (port) {
      authority += `:${port}`;
    }

    return new ClientOAuth2({
      clientId: openIdConfig.clientId,
      accessTokenUri: openIdConfig.token_endpoint,
      authorizationUri: openIdConfig.authorization_endpoint,
      redirectUri: `${protocol}${authority}${authCallback.getLink()}`,
      scopes: ['openid'],
    });
  }, [openIdConfig.authorization_endpoint, openIdConfig.token_endpoint, openIdConfig.clientId]);

  return <AuthClientContext.Provider value={client}>{children}</AuthClientContext.Provider>;
}
